import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/services/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useRedeemOrganizationProductTrial } from '@/api/auth/product-trials/product-trials';
import { getGetOrganizationProductEntitlementsQueryKey } from '@/api/auth/product-entitlements/product-entitlements';
import { useOrgs } from '@/contexts/OrgsContext';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Schema for the simplified redeem trial form (only token input).
 */
const redeemTrialFormSchema = z.object({
  token: z
    .string()
    .min(1, 'Trial code is required')
    .regex(/^[a-zA-Z0-9]{32}$/, 'Trial code must be 32 alphanumeric characters'),
});

type RedeemTrialFormValues = z.infer<typeof redeemTrialFormSchema>;

/**
 * Props for the RedeemTrialDialog component.
 */
interface RedeemTrialDialogProps {
  /** Indicates whether the dialog is open. */
  open: boolean;
  /** Callback invoked when the dialog open state changes. */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog for redeeming a product trial token for the currently selected organization.
 */
export function RedeemTrialDialog({ open, onOpenChange }: RedeemTrialDialogProps) {
  const { selectedOrganization, selectedOrganizationId } = useOrgs();
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RedeemTrialFormValues>({
    resolver: zodResolver(redeemTrialFormSchema),
    defaultValues: {
      token: '',
    },
  });

  const { mutateAsync: redeemForOrg, isPending: isRedeemingForOrg } = useRedeemOrganizationProductTrial();

  const onSubmit = useCallback(
    async (values: RedeemTrialFormValues) => {
      if (!selectedOrganizationId) {
        toast.error(t.dialogs.redeemTrial.noOrganizationSelected);
        return;
      }

      try {
        await redeemForOrg({
          orgId: selectedOrganizationId,
          productId: 'data-mission',
          data: { token: values.token },
        });

        // Invalidate entitlements query to refresh the data
        const entitlementsQueryKey = getGetOrganizationProductEntitlementsQueryKey(
          selectedOrganizationId,
          'data-mission'
        );
        await queryClient.invalidateQueries({ queryKey: entitlementsQueryKey });

        toast.success(t.dialogs.redeemTrial.trialRedeemedSuccessfully);
        reset();
        onOpenChange(false);
      } catch (error: unknown) {
        logger.error(error instanceof Error ? error : new Error(String(error)), {
          context: 'RedeemTrial',
          organizationId: selectedOrganizationId,
        });
        const detail = (error as { response?: { data?: { detail?: string } }; message?: string }).response?.data?.detail
          ?? (error as { message?: string }).message
          ?? t.dialogs.redeemTrial.failedToRedeemTrial;
        toast.error(detail);
      }
    },
    [redeemForOrg, reset, onOpenChange, selectedOrganizationId, queryClient, t],
  );

  const handleDialogOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isSubmitting && !isRedeemingForOrg) {
        reset();
      }
      onOpenChange(newOpen);
    },
    [isRedeemingForOrg, isSubmitting, onOpenChange, reset],
  );

  const submitting = isSubmitting || isRedeemingForOrg;

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t.dialogs.redeemTrial.title}</DialogTitle>
          <DialogDescription>
            {t.dialogs.redeemTrial.description.replace('{orgName}', selectedOrganization?.orgName ?? '')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-6">
          <div className="grid gap-2">
            <Label htmlFor="organization">{t.dialogs.redeemTrial.organization}</Label>
            <div className="rounded-md border px-3 py-2 text-sm bg-muted">
              {selectedOrganization?.orgName}
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="token">{t.dialogs.redeemTrial.trialCode}</Label>
            <Input
              id="token"
              placeholder={t.dialogs.redeemTrial.trialCodePlaceholder}
              maxLength={32}
              autoComplete="off"
              autoFocus
              {...register('token')}
            />
            {errors.token?.message && (
              <p className="text-sm text-destructive">
                {errors.token.message === 'Trial code is required' 
                  ? t.dialogs.redeemTrial.trialCodeRequired
                  : errors.token.message === 'Trial code must be 32 alphanumeric characters'
                  ? t.dialogs.redeemTrial.trialCodeInvalid
                  : errors.token.message}
              </p>
            )}
          </div>

          <DialogFooter className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => { handleDialogOpenChange(false); }} disabled={submitting}>
              {t.dialogs.redeemTrial.cancel}
            </Button>
            <LoadingButton type="submit" isLoading={submitting}>
              {t.dialogs.redeemTrial.redeem}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
